import { Injectable, NgZone } from '@angular/core';
import { Observable, Observer, forkJoin } from 'rxjs';
import { AddressModel } from 'src/app/models/address/address.model';

declare var google: any;

@Injectable({
	providedIn: 'root'
})
export class MapsService {
	private addressDetails: AddressModel;

	constructor(private _ngZone: NgZone) { }

	public getGeocoding(address: any) {
		const self = this;
		return new Observable((observer: Observer<object>) => {
			try {
				const autocm = new google.maps.places.AutocompleteService();
				autocm
					.getPlacePredictions(
						{ input: address, componentRestrictions: { country: 'us' } },
						(predictions, status) => {
							self._ngZone.run(() => {
								if (status === google.maps.places.PlacesServiceStatus.OK) {
									let places$ = [];
									predictions.forEach(x => {
										if (x.place_id) {
											places$.push(this.getGeocodingByPlaceId(x.place_id));
										}
									})

									if (places$.length > 0) {
										forkJoin(places$)
											.subscribe(
												r => {
													var addresses = [];
													if (r && r.length > 0) {
														r.forEach((a: any) => {
															a.forEach(x => {
																if (x.line1) {
																	addresses.push(x);
																}
															})
														})
													}

													observer.next(addresses);
													observer.complete();
												},
												() => {
													observer.error('Failed to get place predictions ' + status);
													observer.complete();
												})
									}
									else {
										observer.next([]);
										observer.complete();
									}

								}
								else {
									observer.error('Failed to get place predictions ' + status);
									observer.complete();
								}
							})
						});
			}
			catch (error) {
				observer.error('Failed to get place predictions ' + error);
				observer.complete();
			}
		});
	}

	public getGeocodingByPlaceId(placeId: string) {
		return new Observable((observer: Observer<object>) => {
			try {
				const geocoder = new google.maps.Geocoder();
				geocoder.geocode(
					{ placeId },
					(results, status) => {
						this.processGeocodingResults(results, status, observer);
						observer.complete();
					}
				);
			} catch (error) {
				observer.error('Failed to get geocode ' + error);
				observer.complete();
			}
		});
	}

	public getGeocodingByCoordinates(lat: number, lng: number) {
		return new Observable((observer: Observer<object>) => {
			try {
				const geocoder = new google.maps.Geocoder();
				geocoder.geocode(
					{ location: { lat, lng } },
					(results, status) => {
						this.processGeocodingResults(results, status, observer);
						observer.complete();
					}
				);
			} catch (error) {
				observer.error('Failed to get geocode ' + error);
				observer.complete();
			}
		});
	}

	private processGeocodingResults(results, status, observer): void {
		if (status === google.maps.GeocoderStatus.OK) {
			const places: AddressModel[] = [];

			results.forEach(element => {
				if (element.address_components.find(c => c.types.find(type => type === 'street_number'))) {
					places.push(this.createAddress(element));
				}
			});

			observer.next(places);
		} else {
			if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
				observer.error('Address not found!');
			} else {
				observer.error(status);
			}
		}
	}

	private createAddress(address) {
		this.addressDetails = new AddressModel();
		if (address.partial_match != null) {
			this.addressDetails.isPartial = address.partial_match;
		}

		const streetNumber = address.address_components.find(comp => comp.types.find(type => type == 'street_number'));
		if (streetNumber != null) {
			this.addressDetails.line1 = streetNumber.long_name;
		}

		const route = address.address_components.find(comp => comp.types.find(type => type == 'route'));
		if (route != null) {
			this.addressDetails.line1 += ' ' + route.long_name;
		}

		const locality = address.address_components.find(comp => comp.types.find(type => type.indexOf('locality') !== -1));
		if (locality != null) {
			this.addressDetails.city = locality.long_name;
			this.addressDetails.details = `${this.addressDetails.city}, `;
		}

		const stateCode = address.address_components.find(comp => comp.types.find(type => type == 'administrative_area_level_1'))
		if (stateCode != null) {
			this.addressDetails.stateName = stateCode.long_name;
			this.addressDetails.stateCode = stateCode.short_name;
			this.addressDetails.details += `${this.addressDetails.stateCode || this.addressDetails.stateName} `;
		}

		const postalCode = address.address_components.find(comp => comp.types.find(type => type == 'postal_code'));
		if (postalCode != null) {
			this.addressDetails.zipCode = postalCode.long_name;
			this.addressDetails.details += `${this.addressDetails.zipCode}`;
		}

		const country = address.address_components.find(comp => comp.types.find(type => type == 'country'));
		if (country != null) {
			this.addressDetails.country = country.long_name;
			this.addressDetails.countryCode = country.short_name;
			this.addressDetails.details += `, ${this.addressDetails.countryCode}`;
		}

		const latitude = address.geometry.location.lat();
		this.addressDetails.latitude = latitude;
		const longitude = address.geometry.location.lng();
		this.addressDetails.longitude = longitude;

		return this.addressDetails;
	}
}
